import { getConfig } from "../constants/config";
import { getTotalAmountOut } from "./getTotalAmountOut";
import { parseUnits, formatUnits } from "ethers-v6";

/**
 * Calculates the minimum amount out for a swap considering slippage tolerance.
 * This function can handle various input types and formats, including routes and raw amounts.
 *
 * @param {Object} params - The parameters object
 * @param {(Array|string|bigint)} params.variable - The input value. Can be:
 *   - Array: A route array from getRoute
 *   - string: A number in string format (raw or formatted)
 *   - bigint: Amount in BigInt format
 * @param {(string|number)} [params.slippageAmount=0.5] - Slippage tolerance. Defaults to config value (0.5%)
 *   - If isBps is false: percentage (e.g., "0.5" for 0.5%)
 *   - If isBps is true: basis points (e.g., "10000" for 100%)
 * @param {Object} [params.options] - Additional options
 * @param {boolean} [params.options.isRaw=true] - If true, amounts are in raw units (wei)
 * @param {boolean} [params.options.isBps=false] - If true, slippage is in basis points
 * @param {number} [params.options.outTokenDecimals=undefined] - Decimals of output token (required if isRaw=false)
 *
 * @returns {bigint} The minimum amount out considering slippage
 *
 * @throws {Error} If variable or slippageAmount is missing/invalid
 * @throws {Error} If slippage calculation fails
 */
export const calculateAmountOutMin = async ({
  variable, // route or amount
  slippageAmount = getConfig().slippage,
  options = {},
}) => {
  // Merge with defaults to handle partial options
  const mergedOptions = {
    isRaw: true,
    isBps: false,
    outTokenDecimals: undefined,
    ...options,
  };

  let amountOut = BigInt(0);
  let slippage = 0;

  if (
    !variable ||
    !slippageAmount ||
    (Array.isArray(variable) && variable?.length === 0)
  ) {
    throw new Error("Invalid or missing `variable` or `slippageAmount` arg.");
  }

  // Validate outTokenDecimals is provided when isRaw=false
  if (!mergedOptions.isRaw && mergedOptions.outTokenDecimals === undefined) {
    throw new Error(
      "outTokenDecimals is required when isRaw=false"
    );
  }

  //If variable is Route
  if (Array.isArray(variable) && variable.length !== 0)
    amountOut = (await getTotalAmountOut(variable)).amountOut;

  //If variable is raw string
  if (typeof variable === "string" && mergedOptions.isRaw)
    amountOut = BigInt(variable);

  // If variable is formatted string
  if (typeof variable === "string" && !mergedOptions.isRaw)
    amountOut = BigInt(parseUnits(variable, mergedOptions.outTokenDecimals));

  // If variable is already bigint
  if (typeof variable === "bigint") amountOut = variable;

  // Validate slippage is not negative
  if (Number(slippageAmount) < 0) {
    throw new Error("Error setting slippage: Slippage amount cannot be negative.");
  }

  // if slippage is formatted string or float/number
  if (
    (typeof slippageAmount === "string" ||
      typeof slippageAmount === "number") &&
    !mergedOptions.isBps
  )
    slippage = (Number(slippageAmount) / 100).toString();
  // If slippage is bps units
  if (mergedOptions.isBps) slippage = (Number(slippageAmount) / 10000).toString();

  if (Number(slippageAmount) === 0) {
    console.error(
      "Slippage is set to 0. Please set slippage to a non-zero value if not intended."
    );
    // Return in requested format
    if (mergedOptions.isRaw) return amountOut;
    return formatUnits(amountOut, mergedOptions.outTokenDecimals);
  }

  if (
    (Number(slippageAmount) >= 100 && !mergedOptions.isBps) ||
    (Number(slippageAmount) >= 10000 && mergedOptions.isBps)
  ) {
    console.error(
      "Slippage is set to 100% or higher. Please set slippage to a lower value if not intended."
    );
    return 0n;
  }

  if (!slippage || amountOut === BigInt(0)) {
    if (!slippage) throw new Error("Error setting slippage!");
    if (amountOut === BigInt(0))
      throw new Error(
        "Error setting slippage: No amount out provided! This might mean swap value is too low. (Return token amount is 0)"
      );
  }

  const integerPart = slippage.split(".")[0] || "0";
  const fractionalPart = slippage.split(".")[1] || "";

  const numerator = (
    integerPart.toString() + fractionalPart.toString()
  ).replace(/^0+|0+$/g, "");

  const denominatorExp = fractionalPart.length;

  let amountOutMin =
    amountOut - (amountOut * BigInt(numerator)) / BigInt(10 ** denominatorExp);

  // NOTE: We do NOT deduct fees here!
  // The smart contract checks minTotalAmountOut BEFORE deducting fees.
  // Fees are deducted by the contract's _processFee function AFTER validation.
  // This ensures slippage protection is based on the swap result, not post-fee amount.
  //
  // Contract flow:
  // 1. Execute swaps → finalTokenAmount
  // 2. Check: finalTokenAmount >= minTotalAmountOut (our calculated value)
  // 3. Process fees: amountToTransfer = finalTokenAmount - fees
  // 4. Transfer amountToTransfer to user
  //
  // Therefore: minTotalAmountOut = amountOut - slippage (NO fee deduction)

  if (amountOutMin < 0n) {
    console.error(
      "Error setting slippage: Minimum amount is negative. This might mean slippage value is higher than 100%. Minimum amount set to `0`."
    );
    return 0n;
  }

  // Return in requested format
  if (mergedOptions.isRaw) return amountOutMin;
  return formatUnits(amountOutMin, mergedOptions.outTokenDecimals);
};
